/**
 * +glide: the glider's wind and the wind chutes' hum. Two looped beds built on the TomMusic wind
 * loop ('amb.wind', the ambience's own clip): the glide wind follows the own hero's flight (louder
 * and higher with speed and sink, gone on the ground), the chute hum is positional (the nearest
 * updraft: swells as you come close, peaks inside it, panned by where it stands on screen) and runs
 * a deeper, slower copy of the same loop so it reads as a rushing column. Pure level maths below
 * (tested); the node plumbing never throws into the game.
 */
import { mixer } from './mixer';
import { samples } from './samples';

/** The glide wind for a flight: gain 0..1 and playback rate. */
export function windLevel(gliding: boolean, speed: number, sink: number, airborne: boolean): { gain: number; rate: number } {
  if (!airborne) return { gain: 0, rate: 1 };
  const s = Math.max(0, speed) + Math.max(0, sink) * 0.8;
  const k = Math.min(1, s / 14);
  return { gain: (gliding ? 0.22 : 0.12) + 0.5 * k, rate: 0.85 + 0.45 * k };
}

/** The hum of a chute of radius r at distance d (yd): 0 past ~40 yd, full inside the column. */
export function chuteLevel(d: number, r: number): number {
  if (d <= r) return 1;
  const k = Math.max(0, 1 - (d - r) / 38);
  return k * k * 0.75;
}

interface Bed {
  src: AudioBufferSourceNode;
  gain: GainNode;
  pan: StereoPannerNode | null;
}

export class GlideAudio {
  private wind: Bed | null = null;
  private hum: Bed | null = null;
  private loading = false;

  /** Per frame. `pan` -1..1 for the chute (screen side). */
  update(wind: { gain: number; rate: number }, chute: { gain: number; pan: number }): void {
    try {
      const ctx = mixer.ctx;
      if (!ctx || ctx.state !== 'running' || mixer.level('sfx') <= 0.0005) return this.silence();
      const buf = samples.loopBuffer('amb.wind');
      if (!buf) {
        if (!this.loading && (wind.gain > 0 || chute.gain > 0)) {
          this.loading = true;
          void samples.loadLoop('amb.wind').finally(() => (this.loading = false));
        }
        return;
      }
      this.wind = this.drive(ctx, buf, this.wind, wind.gain * 0.9, wind.rate, 0, 0.25);
      this.hum = this.drive(ctx, buf, this.hum, chute.gain * 0.7, 0.62, chute.pan, 0.4);
    } catch {
      /* never break the game over wind */
    }
  }

  private drive(ctx: AudioContext, buf: AudioBuffer, bed: Bed | null, gain: number, rate: number, pan: number, tau: number): Bed | null {
    if (!bed && gain <= 0.005) return null;
    if (!bed) {
      const bus = mixer.input('sfx', true);
      if (!bus) return null;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const start = samples.loopStart('amb.wind');
      const dur = samples.loopDuration('amb.wind');
      src.loopStart = start;
      src.loopEnd = dur > 0 && start + dur <= buf.duration + 1e-3 ? start + dur : buf.duration;
      const g = ctx.createGain();
      g.gain.value = 0;
      const p = typeof ctx.createStereoPanner === 'function' ? ctx.createStereoPanner() : null;
      if (p) src.connect(g).connect(p).connect(bus);
      else src.connect(g).connect(bus);
      src.start(ctx.currentTime + 0.02, start + Math.random() * Math.max(0, src.loopEnd - start - 0.1));
      bed = { src, gain: g, pan: p };
    }
    const now = ctx.currentTime;
    bed.gain.gain.setTargetAtTime(Math.max(0, gain), now, tau);
    bed.src.playbackRate.setTargetAtTime(Math.max(0.25, rate), now, 0.3);
    if (bed.pan) bed.pan.pan.setTargetAtTime(Math.max(-1, Math.min(1, pan)), now, 0.2);
    if (gain <= 0.005 && bed.gain.gain.value < 0.01) {
      this.stop(bed);
      return null;
    }
    return bed;
  }

  private stop(b: Bed): void {
    try {
      b.src.stop();
      b.gain.disconnect();
    } catch {
      /* gone */
    }
  }

  silence(): void {
    if (this.wind) this.stop(this.wind);
    if (this.hum) this.stop(this.hum);
    this.wind = this.hum = null;
  }
}
